import { useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

export const Lightbox = ({ items = [], index = null, onClose, onNavigate }) => {
  const open = index !== null && index !== undefined && !!items[index];
  const count = items.length;
  const touchStartX = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (count > 1 && open) {
        if (e.key === "ArrowRight") onNavigate((index + 1) % count);
        if (e.key === "ArrowLeft") onNavigate((index - 1 + count) % count);
      }
    };
    window.addEventListener("keydown", onKey);
    if (open) document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, index, count, onClose, onNavigate]);

  const item = open ? items[index] : null;
  const go = (dir) => onNavigate((index + dir + count) % count);

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null || count < 2) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 48) go(dx < 0 ? 1 : -1);
    touchStartX.current = null;
  };

  const arrowClass =
    "absolute top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-black/60 text-neutral-200 backdrop-blur-md transition-[border-color,color,transform] duration-300 hover:scale-105 hover:border-electric hover:text-electric";

  return (
    <AnimatePresence>
      {item && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/95 p-4 backdrop-blur-md"
          onClick={onClose}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          role="dialog"
          aria-modal="true"
          aria-label={item.caption || "Media viewer"}
          data-testid="lightbox-overlay"
        >
          <button
            type="button"
            onClick={onClose}
            aria-label="Close viewer"
            data-testid="lightbox-close"
            className="absolute right-5 top-5 z-10 flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-black/60 text-neutral-200 backdrop-blur-md transition-[border-color,color] duration-300 hover:border-electric hover:text-electric"
          >
            <X size={20} aria-hidden="true" />
          </button>

          {count > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); go(-1); }}
                aria-label="Previous"
                data-testid="lightbox-prev"
                className={`${arrowClass} left-3 sm:left-6`}
              >
                <ChevronLeft size={24} aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); go(1); }}
                aria-label="Next"
                data-testid="lightbox-next"
                className={`${arrowClass} right-3 sm:right-6`}
              >
                <ChevronRight size={24} aria-hidden="true" />
              </button>
              <span
                className="absolute left-5 top-7 z-10 text-xs font-bold uppercase tracking-[0.25em] text-neutral-400"
                data-testid="lightbox-counter"
              >
                {index + 1} / {count}
              </span>
            </>
          )}

          <motion.div
            key={item.video || item.image}
            initial={{ scale: 0.94, y: 16, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.94, y: 16, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-5xl"
          >
            {item.video ? (
              <video
                poster={item.poster}
                controls
                autoPlay
                playsInline
                className="max-h-[80vh] w-full rounded-2xl border border-white/10 bg-black"
                data-testid="lightbox-video"
              >
                <source src={item.video} type="video/mp4" />
                <source src={item.video.replace(".mp4", ".webm")} type="video/webm" />
              </video>
            ) : (
              <img
                src={item.image}
                alt={item.caption || ""}
                className="mx-auto max-h-[80vh] rounded-2xl border border-white/10 object-contain"
                data-testid="lightbox-image"
              />
            )}
            {item.caption && (
              <p className="mt-4 text-center text-sm font-semibold text-neutral-300" data-testid="lightbox-caption">
                {item.caption}
              </p>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
